import React, { useState, useEffect } from 'react';
import { calculateIRSShortfall } from '../lib/fluxUtils';
import { TrendingUp, Calculator, ShieldCheck, ArrowRight } from 'lucide-react';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { Resource } from '../types';
import { TipPoolCalculator } from './TipPoolCalculator';

export const PublicInteractiveTools: React.FC = () => {
  const [gross, setGross] = useState<string>('');
  const [reported, setReported] = useState<string>('');
  const [employeeName, setEmployeeName] = useState<string>('');
  const [disputeAmount, setDisputeAmount] = useState<string>('');
  const [disputeType, setDisputeType] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [period, setPeriod] = useState<string>('');
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [resources, setResources] = useState<Resource[]>([]);

  useEffect(() => {
    const q = query(collection(db, 'resources'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setResources(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Resource)));
    });
    return unsubscribe;
  }, []);
  
  const irsResult = calculateIRSShortfall(parseFloat(gross) || 0, parseFloat(reported) || 0);

  const getWorkflow = () => {
    switch (disputeType) {
      case 'Incorrect Hours Logged':
        return 'Your manager will verify the timeclock logs against the shift report. Expected resolution: 24-48 hours.';
      case 'Missing Tip Pool Contribution':
        return 'Payroll will audit the daily tip pool intake records. Expected resolution: 3-5 business days.';
      case 'Calculation Error':
        return 'Management will re-run the distribution formula based on the verified hours. Expected resolution: 24 hours.';
      default:
        return 'Please contact your manager to formalize this claim.';
    }
  };

  return (
    <div className="py-16 bg-gray-50 border-y border-gray-100" id="tools">
      <div className="max-w-7xl mx-auto px-6">
        <h2 className="text-3xl font-black text-gray-900 text-center mb-12">Public Compliance & Analysis Tools</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          
          {/* Tip Pool Calculator */}
          <TipPoolCalculator compact={true} />

          {/* IRS Shortfall Estimator */}
          <div className="card p-6 bg-white shadow-sm border border-gray-100">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><ShieldCheck size={20} className="text-emerald-600"/> IRS 8% Threshold</h3>
            <div className="text-xs text-gray-500 mb-4">
              A large food or beverage establishment is one that:
              <ul className="list-disc pl-4 mt-1 space-y-1">
                <li>Is located in the 50 states or DC.</li>
                <li>Provides food/beverage for consumption on-premises (not fast food).</li>
                <li>Customarily receives tips.</li>
                <li>Employed &gt;10 employees on a typical business day in the preceding year.</li>
              </ul>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Period / Range</label>
                <select value={period} onChange={e => setPeriod(e.target.value)} className="w-full p-2 border rounded">
                  <option value="">Select a period...</option>
                  <option value="last-pay-period">Last pay period</option>
                  <option value="last-quarter">Last quarter</option>
                  <option value="last-year">Last year</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gross Receipts ($)</label>
                <input type="number" placeholder="e.g. 10000" value={gross} onChange={e => setGross(e.target.value)} className="w-full p-2 border rounded" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reported Tips ($)</label>
                <input type="number" placeholder="e.g. 700" value={reported} onChange={e => setReported(e.target.value)} className="w-full p-2 border rounded" />
              </div>
              {(gross || reported) && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-2 text-sm">
                  <p className="text-gray-600">8% Threshold: <span className="font-semibold">${irsResult.threshold.toFixed(2)}</span></p>
                  {irsResult.isShortfall ? (
                    <p className="text-rose-600 font-bold">⚠️ True Up and Form 8027 Required (${irsResult.shortfall.toFixed(2)} Shortfall)</p>
                  ) : (
                    <p className="text-emerald-600 font-bold">✅ Form 8027 Not Required</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Tip Dispute Tool */}
          <div className="card p-6 bg-white shadow-sm border border-gray-100">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><TrendingUp size={20} className="text-amber-600"/> Tip Dispute Simulator</h3>
            <p className="text-xs text-gray-500 mb-4">Visualize the resolution process for your tip allocation dispute.</p>
            <div className="space-y-4">
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">Employee Name</label>
                 <select value={employeeName} onChange={e => { setEmployeeName(e.target.value); setIsSubmitted(false); }} className="w-full p-2 border rounded">
                   <option value="">Select an employee...</option>
                   <option value="Jane Doe">Jane Doe</option>
                   <option value="John Smith">John Smith</option>
                   <option value="Alice Johnson">Alice Johnson</option>
                 </select>
               </div>
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">Dispute Type</label>
                 <select value={disputeType} onChange={e => { setDisputeType(e.target.value); setIsSubmitted(false); }} className="w-full p-2 border rounded">
                   <option value="">Select a type...</option>
                   <option>Incorrect Hours Logged</option>
                   <option>Missing Tip Pool Contribution</option>
                   <option>Calculation Error</option>
                 </select>
               </div>
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">Dispute Amount ($)</label>
                 <input type="number" placeholder="e.g. 50" value={disputeAmount} onChange={e => { setDisputeAmount(e.target.value); setIsSubmitted(false); }} className="w-full p-2 border rounded" />
               </div>
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                 <textarea placeholder="Briefly describe the issue..." value={description} onChange={e => { setDescription(e.target.value); setIsSubmitted(false); }} className="w-full p-2 border rounded" rows={3} />
               </div>
               <button
                 onClick={() => setIsSubmitted(true)}
                 className="w-full bg-amber-600 text-white font-bold py-2 px-4 rounded hover:bg-amber-700 transition"
               >
                 Submit Dispute
               </button>
               {(isSubmitted && disputeType && disputeAmount && description) && (
                 <div className="mt-4 p-4 bg-amber-50 rounded-lg text-sm">
                    <p className="text-amber-800 font-semibold mb-1">Resolution Workflow:</p>
                    <p className="text-amber-700">{getWorkflow()}</p>
                 </div>
               )}
            </div>
          </div>
          {/* Compliance Resources */}
          {/*
          <div className="card p-6 bg-white shadow-sm border border-gray-100">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><ShieldCheck size={20} className="text-emerald-600"/> Compliance Resources</h3>
            <p className="text-xs text-gray-500 mb-4">Get free tip policy templates and payroll checklists.</p>
            <div className="space-y-4">
              <input type="email" placeholder="Enter your email" value={email} onChange={e => { setEmail(e.target.value); setShowConfirmation(false); }} className="w-full p-2 border rounded" />
              <button
                onClick={async () => {
                  if (email) {
                    try {
                      await fetch('/api/waitlist', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ email }),
                      });
                      setShowConfirmation(true);
                      setEmail('');
                    } catch (error) {
                      console.error('Failed to send email', error);
                      alert('Failed to send email, please try again.');
                    }
                  }
                }}
                className="w-full bg-emerald-600 text-white font-bold py-2 px-4 rounded hover:bg-emerald-700 transition"
              >
                Send Download Link
              </button>
              {showConfirmation && (
                <div className="mt-4 p-4 bg-emerald-50 rounded-lg text-sm space-y-2">
                  <p className="text-emerald-800 font-medium">Thank you! You can download your resources now:</p>
                  {resources.map(res => (
                    <a key={res.id} href={res.fileUrl} target="_blank" rel="noopener noreferrer" className="block text-emerald-700 underline">{res.title}</a>
                  ))}
                </div>
              )}
            </div>
          </div>
          */}
        </div>
      </div>
    </div>
  );
};
